// src/ui/components/helpers/extractFirstMedia.ts
type ExtractOpts = {
  onlyVideo?: boolean;
  fallbackToIframe?: boolean;
  debug?: boolean; // activa logs adicionales si quieres
};

const decodeHtmlEntities = (str: string) => {
  // simple decoder usando textarea (browser)
  if (typeof window === 'undefined') return str;
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
};

const regexFind = (input: string) => {
  // regex robustos para encontrar bloques <video>...</video>, <iframe ...></iframe> o <img ...>
  const videoRe = /<video[\s\S]*?<\/video>/i;
  const iframeRe = /<iframe[\s\S]*?<\/iframe>/i;
  const imgRe = /<img[^>]*>/i;

  const v = input.match(videoRe);
  if (v) return { tag: 'video', match: v[0] };
  const i = input.match(iframeRe);
  if (i) return { tag: 'iframe', match: i[0] };
  const im = input.match(imgRe);
  if (im) return { tag: 'img', match: im[0] };
  return null;
};

export const extractFirstMedia = (input: string, opts: ExtractOpts = {}) => {
  const { onlyVideo = false, fallbackToIframe = false, debug = true } = opts;
  if (!input) return { mediaHtml: '', contentHtml: input };

  // Quick check: ¿contiene literalmente <iframe o <video ?
  const hasIframeLiteral = input.indexOf('<iframe') !== -1;
  const hasVideoLiteral = input.indexOf('<video') !== -1;
  if (debug) console.debug('[extractFirstMedia] hasLiteral', { hasIframeLiteral, hasVideoLiteral });

  // 1) DOMParser normal
  if (typeof window !== 'undefined' && 'DOMParser' in window) {
    try {
      const parser = new DOMParser();
      let doc = parser.parseFromString(input, 'text/html');

      // selector según opciones
      let selectors: string[] = [];

      if (onlyVideo) {
        selectors = ['video'];
        if (fallbackToIframe) selectors.push('iframe');
      } else {
        selectors = ['video', 'iframe', 'img'];
      }
      const sel = selectors.join(',');
      let node = doc.body.querySelector(sel);

      // 2) Si no encontramos nada, puede que el HTML venga escapado (&lt;iframe&gt;). Intentamos decodificar y parsear de nuevo
      if (!node) {
        const decoded = decodeHtmlEntities(input);
        if (decoded !== input) {
          if (debug) console.debug('[extractFirstMedia] trying decodeHtmlEntities');
          doc = parser.parseFromString(decoded, 'text/html');
          node = doc.body.querySelector(sel);
        }
      }

      // 3) Si seguimos sin node, intentamos detectar con regex dentro del HTML (último recurso)
      if (!node) {
        if (debug)
          console.debug('[extractFirstMedia] DOMParser found nothing, trying regex search');
        const found = regexFind(input) || regexFind(decodeHtmlEntities(input));
        if (found) {
          const mediaHtml = found.match;
          const contentHtml = input.replace(mediaHtml, '');
          return { mediaHtml, contentHtml };
        }
        return { mediaHtml: '', contentHtml: input };
      }

      // Si encontramos nodo por DOMParser
      const mediaHtml = node.outerHTML;
      node.remove();
      const contentHtml = doc.body.innerHTML || '';
      return { mediaHtml, contentHtml };
    } catch (e) {
      if (debug) console.error('[extractFirstMedia] DOMParser error', e);
      // fallback a regex abajo
    }
  }

  // SSR o falló DOMParser -> regex fallback
  if (debug) console.debug('[extractFirstMedia] using regex fallback');
  if (onlyVideo) {
    const videoMatch = input.match(/<video[\s\S]*?<\/video>/i);
    if (videoMatch) {
      const mediaHtml = videoMatch[0];
      const contentHtml = input.replace(mediaHtml, '');
      return { mediaHtml, contentHtml };
    }
    if (fallbackToIframe) {
      const iframeMatch = input.match(/<iframe[\s\S]*?<\/iframe>/i);
      if (iframeMatch) {
        const mediaHtml = iframeMatch[0];
        const contentHtml = input.replace(mediaHtml, '');
        return { mediaHtml, contentHtml };
      }
    }
    return { mediaHtml: '', contentHtml: input };
  } else {
    const videoMatch = input.match(/<video[\s\S]*?<\/video>/i);
    if (videoMatch) {
      const mediaHtml = videoMatch[0];
      const contentHtml = input.replace(mediaHtml, '');
      return { mediaHtml, contentHtml };
    }
    const iframeMatch = input.match(/<iframe[\s\S]*?<\/iframe>/i);
    if (iframeMatch) {
      const mediaHtml = iframeMatch[0];
      const contentHtml = input.replace(mediaHtml, '');
      return { mediaHtml, contentHtml };
    }
    const imgMatch = input.match(/<img[^>]*>/i);
    if (imgMatch) {
      const mediaHtml = imgMatch[0];
      const contentHtml = input.replace(mediaHtml, '');
      return { mediaHtml, contentHtml };
    }
    // también buscar links youtube "bare" (enlace dentro de texto) y construir iframe como último recurso:
    const ytMatch =
      input.match(/https?:\/\/(www\.)?youtube\.com\/watch\?v=([A-Za-z0-9_-]{6,})/i) ||
      input.match(/https?:\/\/youtu\.be\/([A-Za-z0-9_-]{6,})/i);
    if (ytMatch) {
      const id = ytMatch[2] || ytMatch[1];
      const mediaHtml = `<iframe src="https://www.youtube.com/embed/${id}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>`;
      const contentHtml = input.replace(ytMatch[0], '');
      return { mediaHtml, contentHtml };
    }

    return { mediaHtml: '', contentHtml: input };
  }
};
